import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EquipmentType,
  EquipmentServiceRate,
  EquipmentTypeCreate,
  EquipmentTypeUpdate,
  EquipmentServiceRateCreate,
  EquipmentServiceRateUpdate,
} from '@jobos/shared';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const equipmentCatalogKeys = {
  all: ['equipment-catalog'] as const,
};

// ---------------------------------------------------------------------------
// Shared catalog query — GET /api/settings/equipment-catalog
// ---------------------------------------------------------------------------

interface EquipmentCatalogResponse {
  types: EquipmentType[];
  serviceRates: EquipmentServiceRate[];
}

export function useEquipmentCatalog() {
  return useQuery({
    queryKey: equipmentCatalogKeys.all,
    queryFn: () =>
      apiClient.get<EquipmentCatalogResponse>('/settings/equipment-catalog'),
  });
}

// ---------------------------------------------------------------------------
// Equipment Types — slice + mutations
// ---------------------------------------------------------------------------

export function useEquipmentTypes() {
  const catalog = useEquipmentCatalog();
  return {
    ...catalog,
    data: catalog.data?.types,
  };
}

export function useAddEquipmentType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: EquipmentTypeCreate) =>
      apiClient.post<{ item: EquipmentType }>(
        '/settings/equipment-catalog/types',
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentCatalogKeys.all });
      toast({ title: 'Equipment type added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add equipment type.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateEquipmentType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & EquipmentTypeUpdate) =>
      apiClient.put<{ item: EquipmentType }>(
        `/settings/equipment-catalog/types/${id}`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentCatalogKeys.all });
      toast({ title: 'Equipment type updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update equipment type.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteEquipmentType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/settings/equipment-catalog/types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentCatalogKeys.all });
      toast({ title: 'Equipment type deleted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete equipment type.',
        variant: 'destructive',
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Equipment Service Rates — slice + mutations
// ---------------------------------------------------------------------------

export function useEquipmentServiceRates() {
  const catalog = useEquipmentCatalog();
  return {
    ...catalog,
    data: catalog.data?.serviceRates,
  };
}

export function useAddEquipmentServiceRate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: EquipmentServiceRateCreate) =>
      apiClient.post<{ item: EquipmentServiceRate }>(
        '/settings/equipment-catalog/service-rates',
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentCatalogKeys.all });
      toast({ title: 'Service rate added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add service rate.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateEquipmentServiceRate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & EquipmentServiceRateUpdate) =>
      apiClient.put<{ item: EquipmentServiceRate }>(
        `/settings/equipment-catalog/service-rates/${id}`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentCatalogKeys.all });
      toast({ title: 'Service rate updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update service rate.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteEquipmentServiceRate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/settings/equipment-catalog/service-rates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentCatalogKeys.all });
      toast({ title: 'Service rate deleted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete service rate.',
        variant: 'destructive',
      });
    },
  });
}
